/**
 * Simple Rich Text Editor Component
 * 
 * A lightweight TinyMCE-like editor using contenteditable
 * Works without WordPress dependencies
 */
import { useRef, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const SimpleRichEditor = ({ 
    value = '', 
    onChange, 
    placeholder = __( 'Write something...', 'changeloger' ),
    minHeight = 150
}) => {
    const editorRef = useRef(null);

    // Initialize content
    useEffect(() => {
        if (editorRef.current && value && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, []);

    // Handle content changes
    const handleInput = () => {
        if (editorRef.current) {
            const content = editorRef.current.innerHTML;
            if (onChange) {
                onChange(content);
            }
        }
    };

    // Execute formatting command
    const execCommand = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        handleInput();
    };

    // Handle toolbar buttons
    const handleBold = () => execCommand('bold');
    const handleItalic = () => execCommand('italic');
    const handleUnderline = () => execCommand('underline');
    const handleOrderedList = () => execCommand('insertOrderedList');
    const handleUnorderedList = () => execCommand('insertUnorderedList');
    
    const handleLink = () => {
        const url = prompt(__( 'Enter URL:', 'changeloger' ));
        if (url) {
            execCommand('createLink', url);
        }
    };

    const handleClearFormatting = () => execCommand('removeFormat');

    // Check if format is active
    const isFormatActive = (command) => {
        try {
            return document.queryCommandState(command);
        } catch {
            return false;
        }
    };

    return (
        <div className="cha-simple-editor">
            {/* Toolbar */}
            <div className="cha-editor-toolbar">
                <button
                    type="button"
                    className={`cha-toolbar-btn ${isFormatActive('bold') ? 'active' : ''}`}
                    onClick={handleBold}
                    title={__( 'Bold (Ctrl+B)', 'changeloger' )}
                >
                    <strong>B</strong>
                </button>
                <button
                    type="button"
                    className={`cha-toolbar-btn ${isFormatActive('italic') ? 'active' : ''}`}
                    onClick={handleItalic}
                    title={__( 'Italic (Ctrl+I)', 'changeloger' )}
                >
                    <em>I</em>
                </button>
                <button
                    type="button"
                    className={`cha-toolbar-btn ${isFormatActive('underline') ? 'active' : ''}`}
                    onClick={handleUnderline}
                    title={__( 'Underline (Ctrl+U)', 'changeloger' )}
                >
                    <u>U</u>
                </button>
                
                <span className="cha-toolbar-divider"></span>
                
                <button
                    type="button"
                    className="cha-toolbar-btn"
                    onClick={handleUnorderedList}
                    title={__( 'Bullet List', 'changeloger' )}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                        <line x1="8" y1="6" x2="21" y2="6" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="8" y1="12" x2="21" y2="12" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="8" y1="18" x2="21" y2="18" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="4" cy="6" r="1.5" fill="currentColor"/>
                        <circle cx="4" cy="12" r="1.5" fill="currentColor"/>
                        <circle cx="4" cy="18" r="1.5" fill="currentColor"/>
                    </svg>
                </button>
                <button
                    type="button"
                    className="cha-toolbar-btn"
                    onClick={handleOrderedList}
                    title={__( 'Numbered List', 'changeloger' )}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                        <line x1="10" y1="6" x2="21" y2="6" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="10" y1="12" x2="21" y2="12" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="10" y1="18" x2="21" y2="18" strokeWidth="2" strokeLinecap="round"/>
                        <text x="3" y="8" fontSize="8" fill="currentColor" fontWeight="bold">1</text>
                        <text x="3" y="14" fontSize="8" fill="currentColor" fontWeight="bold">2</text>
                        <text x="3" y="20" fontSize="8" fill="currentColor" fontWeight="bold">3</text>
                    </svg>
                </button>
                
                <span className="cha-toolbar-divider"></span>
                
                <button
                    type="button"
                    className="cha-toolbar-btn"
                    onClick={handleLink}
                    title={__( 'Insert Link', 'changeloger' )}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                </button>
                
                <button
                    type="button"
                    className="cha-toolbar-btn"
                    onClick={handleClearFormatting}
                    title={__( 'Clear Formatting', 'changeloger' )}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 7V4h16v3"/>
                        <path d="M9 20h6"/>
                        <path d="M12 4v16"/>
                    </svg>
                </button>
            </div>

            {/* Editable Area */}
            <div className="cha-editor-content-wrapper">
                <div
                    ref={editorRef}
                    className="cha-editor-content"
                    contentEditable
                    onInput={handleInput}
                    onBlur={handleInput}
                    style={{ minHeight: `${minHeight}px` }}
                    data-placeholder={placeholder}
                />
            </div>
        </div>
    );
};

export default SimpleRichEditor;
